import "./email-form.scss";
import { autoinject, bindable, bindingMode } from "aurelia-framework";
import { appLogger } from "../../infrastructure/logging";
import {
  ChallengeService,
  IGetEndUserByEmailResult
} from "./../../services/challenge-service";
import { ExternalAuthService } from "../../services/external-auth-service";
import { SSOType } from "../../enums/sso-type";
import {
  ValidationController,
  ValidationControllerFactory,
  validateTrigger,
  ValidationRules
} from "aurelia-validation";
import { LoginStage } from "../../enums/login-stage";
import { isEmailUnderToggle } from "../../utilities/under-toggle";
import { BootstrapFormRenderer } from "../../components/bootstrap-form-renderer";
import dayjs, { Dayjs } from "dayjs";
import { qs } from "url-parse";

@autoinject
export class EmailForm {
  // fromView -subsequent updates made to stage in the parent doesn't flow into the child. Hence, we have bindingMode for stage as fromView
  @bindable({ defaultBindingMode: bindingMode.fromView })
  public stage: LoginStage;
  @bindable({ defaultBindingMode: bindingMode.twoWay })
  public email: string;
  @bindable({ defaultBindingMode: bindingMode.toView })
  public returnUrl: string;
  @bindable({ defaultBindingMode: bindingMode.toView })
  public hasAppLock: boolean = false;

  private _challengeService: ChallengeService;
  private _externalAuthService: ExternalAuthService;
  public hasError: boolean = false;
  public hasDomainError: boolean = false;
  public isLoading: boolean = false;
  public emailFocused: boolean = true;
  public rememberMe : boolean = false;
  public emailController: ValidationController;
  public today: Dayjs = dayjs();
  public greetingMessage: string;

  public constructor(
    challengeService: ChallengeService,
    externalAuthService: ExternalAuthService,
    controllerFactory: ValidationControllerFactory
  ) {
    this._challengeService = challengeService;
    this._externalAuthService = externalAuthService;
    this.emailController = controllerFactory.create();
    this.emailController.validateTrigger = validateTrigger.changeOrBlur;
    this.emailController.addObject(this, emailRules);
    this.emailController.addRenderer(new BootstrapFormRenderer());
  }

  public attached() {
    
    const getEmail = JSON.parse(localStorage.getItem("emailSave"));
    
    if (this.returnUrl) {
      let index = this.returnUrl.indexOf("?");
      let paramForURLSearch = this.returnUrl.substring(index + 1);
      const urlParams = new URLSearchParams(paramForURLSearch);
      var acr_values = urlParams.get("acr_values");
      this.hasAppLock = acr_values == "locked";
    }

    if ( getEmail ) {
      this.email = getEmail;
      this.rememberMe = true;
    }

    // this is for extracting the login hint from the url, which is the email of the user being redirected to Identity.
    let fullQueryString = new URL(location.href).search;
    if(fullQueryString !== null && fullQueryString !== '') {
      // The login hint is in a second query string nested within the full one,
      // so we are splitting by the url encoded ? to get the second query string
      let splitQueryString = fullQueryString.split('%3F');
      if(splitQueryString.length > 1) {
        let parameters = qs.parse(decodeURIComponent(splitQueryString[1]));
        if(parameters != null && !!parameters.login_hint) {
          this.email = parameters.login_hint;
          // disable remember me storage if identity sent a login_hint.
          this.rememberMe = false;
        }
      }
    }

    this.greetingMessage = this.getGreetingMessage();
  }

  public async verifyEmail() {
    let res = await this.emailController.validate();
    if (!res.valid) {
      this.hasError = true;
      return;
    }

    let result: IGetEndUserByEmailResult = this.email.indexOf("@") === -1
      ? await this.submitDomain()
      : await this.submitEmail();

    if (!result) {
      return;
    }

    if (result.ssoType === SSOType.oneLogin) {
      this._externalAuthService.setSsoType(result.ssoType);
      this._externalAuthService.setSsoReturnUrl(this.returnUrl);
      window.location.replace(result.redirectUrl);
    }
    else if (result.ssoType === SSOType.azureAD) {
      this._externalAuthService.setSsoType(result.ssoType);
      this._externalAuthService.setSsoReturnUrl(this.returnUrl);
      window.location.replace(result.redirectUrl);
    }
    else if (result.ssoType === SSOType.googleWorkspace) {
      this._externalAuthService.setSsoType(result.ssoType);
      this._externalAuthService.setSsoReturnUrl(this.returnUrl);
      window.location.replace(result.redirectUrl);
    }
    else if (result.ssoType === SSOType.okta) {
      this._externalAuthService.setSsoType(result.ssoType);
      this._externalAuthService.setSsoReturnUrl(this.returnUrl);
      window.location.replace(result.redirectUrl);
    }
    else if (result.ssoType === SSOType.axxess) {
      this.stage = LoginStage.password;
    }
  }

  public async submitEmail() {
    if(JSON.parse(localStorage.getItem("emailSave")) && this.rememberMe===false){
         localStorage.removeItem('emailSave')
    }
    if(this.rememberMe == true) {
      localStorage.setItem("emailSave", JSON.stringify(this.email));
    }

    this.isLoading = true;
    let result = await this._challengeService.challengeEmail(this.email);
    this.isLoading = false;
    this.hasError = result == null;
    this.hasDomainError = false;
    if (this.hasError) {
      this.stage = LoginStage.email;
      this.emailFocused = true;
      this.email = null;
      appLogger.warn("Email check failed.");
    }
    isEmailUnderToggle(this.email);
    return this.hasError ? null : result;
  }

  public getGreetingMessage() {
    let curHr = parseInt(this.today.format("H"), 10);
    if (curHr < 12) {
        return "Good Morning";
    } else if (curHr < 18) {
        return "Good Afternoon";
    } else {
        return "Good Evening";
    }
}

  public async submitDomain() {
    let domain = this.email;
    if(this.rememberMe == true) {
      localStorage.setItem("emailSave", JSON.stringify(domain));
    }
    this.isLoading = true;
    let result = await this._challengeService.challengeDomain(domain);
    this.isLoading = false;
    this.hasDomainError = result == null;
    this.hasError = false;
    if (this.hasDomainError) {
      this.stage = LoginStage.email;
      this.emailFocused = true;
      this.email = null;
      appLogger.warn("Domain check failed.");

    }
    return this.hasDomainError ? null : result;
  }
}


const emailRules = ValidationRules.ensure((x: EmailForm) => x.email)
  .required()
  .minLength(5)
  .maxLength(256)
  .satisfies((value: string) => {
    if (!value) {
      return true;
    }

    if (value.indexOf("@") === -1) {
      return /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,50}$/.test(value);
    }

    if (/@gmail\.com$/i.test(value)) {
      return /^[a-zA-Z0-9._'+-]+@gmail\.com$/i.test(value);
    }

    return /^[a-zA-Z0-9._'+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,50}$/.test(value);
  }).rules;
